import React from "react";

import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import StatsAPI from "@/src/api/StatsAPI";

import StatCard, {
  formatCountOrEmpty,
  formatDurationOrEmpty,
} from "../kpi/StatCard";

import type { JSX } from "react";

import type { GetContentMediaKpiResponse } from "@/src/api/StatsAPI.types";

type ContentLibraryKpi = GetContentMediaKpiResponse["data"][number];

const KPI_KEY_LIST = ["total", "hosted", "live", "fast", "external"] as const;

function getMetric(
  kpi: ContentLibraryKpi,
  key: (typeof KPI_KEY_LIST)[number],
): { count: number; durationMs: number | null } {
  if (key === "total") {
    return { count: kpi.total.count, durationMs: kpi.total.duration };
  }
  const { count, duration_ms: durationMs } = kpi.per_type[key];
  return { count, durationMs };
}

function ContentLibraryKpiGrid(): JSX.Element {
  const { projectSlug } = useParams<{ projectSlug: string }>();
  const { t, i18n } = useTranslation("editor-analytics");
  const lang = i18n.language;
  const kpiResult = useQuery(
    StatsAPI.generateQuery({
      fn: StatsAPI.getContentMediaKpi,
      params: [{ projectSlug }],
    }),
  );
  const kpi = kpiResult.data?.data[0];

  return (
    <div className="grid grid-cols-2 gap-4 @3xl:grid-cols-5">
      {KPI_KEY_LIST.map((kpiKey) => {
        const metric = kpi ? getMetric(kpi, kpiKey) : undefined;
        return (
          <StatCard
            key={kpiKey}
            label={t(`content_kpi_${kpiKey}`)}
            query={kpiResult}
            scope="asOfToday"
            value={formatCountOrEmpty(metric?.count, lang)}
            footer={
              metric?.durationMs == null
                ? undefined
                : formatDurationOrEmpty(metric.durationMs / 1000)
            }
          />
        );
      })}
    </div>
  );
}

export default ContentLibraryKpiGrid;
